'use client'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import Image from 'next/image'

import {
  Carousel,
  CarouselContent,
  CarouselItem,
} from '@/components/ui/carousel'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { ImageHelper } from '@/lib/helpers/image.helper'
import { GuideExample, GuideSteps, StepBadge } from './guide-steps'
import { PlatformGuide } from './platform-guide.config'

interface PlatformGuideDialogProps {
  guide: PlatformGuide | null
  onOpenChange: (open: boolean) => void
}

export function PlatformGuideDialog({
  guide,
  onOpenChange,
}: PlatformGuideDialogProps) {
  return (
    <Dialog open={!!guide} onOpenChange={onOpenChange}>
      {guide && <PlatformGuideDialogContent guide={guide} />}
    </Dialog>
  )
}

// Mobile: bottom sheet, bước liệt kê phía trên và dãy ảnh vuốt ngang.
// Desktop: dialog giữa màn, mỗi cột gồm bước + ảnh tương ứng.
function PlatformGuideDialogContent({ guide }: { guide: PlatformGuide }) {
  const steps = guide.detailSteps ?? guide.steps

  return (
    <DialogContent
      showCloseButton={false}
      aria-describedby={undefined}
      className='block max-w-none gap-0 bg-white p-0 max-md:bottom-0 max-md:left-0 max-md:top-auto max-md:translate-x-0 max-md:translate-y-0 max-md:rounded-t-2xl max-md:data-[state=closed]:slide-out-to-bottom max-md:data-[state=open]:slide-in-from-bottom md:w-[calc(100%-48px)] md:max-w-[1000px] md:rounded-2xl'
    >
      <DialogPrimitive.Close
        aria-label='Đóng'
        className='absolute -top-6 right-2 z-10 flex size-[50px] items-center justify-center rounded-full border-2 border-white bg-neutral-900 text-white md:-right-6 md:-top-5'
      >
        <X className='size-6' />
      </DialogPrimitive.Close>

      {/* --guide-img-h: chiều cao ảnh co theo viewport để toàn bộ popup nằm
          trong 1 màn hình (phần trừ đi = tiêu đề + các bước + ví dụ + padding).
          overflow-y-auto chỉ là fallback cho màn quá thấp (vd. xoay ngang). */}
      <div className='max-h-[calc(100dvh-40px)] overflow-y-auto p-5 [--guide-img-h:clamp(160px,calc(100dvh-430px),330px)] md:max-h-[calc(100dvh-48px)] md:px-9 md:py-10 md:[--guide-img-h:clamp(240px,calc(100dvh-370px),623px)]'>
        <DialogTitle className='font-criteria text-xl font-semibold tracking-[-0.02em] text-neutral-900 md:text-2xl'>
          Cách lấy mã đơn hàng trên {guide.displayName}
        </DialogTitle>

        <GuideSteps
          steps={steps}
          className='mt-4 gap-4 md:hidden'
          textClassName='text-base'
        />

        <Carousel opts={{ align: 'start' }} className='mt-4 md:mt-6'>
          <CarouselContent className='-ml-2 md:-ml-4'>
            {guide.imagePaths.map((path, i) => (
              <CarouselItem
                key={path}
                className='basis-auto cursor-default pl-2 md:flex md:basis-1/3 md:flex-col md:pl-4'
              >
                {/* flex-1 để ảnh 3 cột thẳng hàng khi text bước dài ngắn khác nhau */}
                <div className='mb-3 hidden items-start gap-1 md:flex md:flex-1'>
                  <StepBadge index={i + 1} />
                  <span className='text-base text-neutral-700'>{steps[i]}</span>
                </div>
                <Image
                  src={ImageHelper.getImageUrl(path)}
                  alt={`Bước ${i + 1}: ${steps[i]}`}
                  width={299}
                  height={623}
                  className='mx-auto h-auto max-h-[var(--guide-img-h)] w-auto max-w-full rounded-2xl border border-neutral-200 md:rounded-3xl'
                />
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        <GuideExample
          text={guide.exampleText}
          code={guide.exampleCode}
          className='mt-4 text-base'
        />
      </div>
    </DialogContent>
  )
}
